import {
  Box,
  Button,
  DiscoveryIndicator,
  Icon,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarPane,
  SidebarPaneBody,
  SidebarPaneHeader,
  SidebarSeparator,
  Text,
  type IconName,
} from "@salestrak/ui";
import React from "react";

import { useAppData } from "#app/lib/data";
import { useCustomFieldsDialogActions } from "#app/lib/custom-fields-dialog-context";
import { useIntegrationsDialogActions } from "#app/lib/integrations-dialog-context";
import { useIntegrationCatalog } from "#app/components/integrations/use-integration-catalog";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useDiscovery } from "#app/lib/use-discovery";
import { usePermission } from "#app/lib/hooks/use-permission";
import { useCloseSidebarIfMobile } from "#app/lib/mobile-sidebar-context";
import { EntityTag } from "#app/components/entity";
import { canEdit } from "#app/lib/utils/access-level";
import { SidebarSection } from "@salestrak/ui";
import { useSidebarResize } from "#app/components/navigation/use-sidebar-resize";
import { useSidebarNav } from "./use-sidebar-nav";
import { WorkflowItem } from "./workflow-item";
import type { Omittable } from "@salestrak/ui";

type NavItemData = Omittable<{
  icon: IconName;
  label: string;
  route: string;
  badge?: number;
  exact?: boolean;
}>;

const USER_SETTINGS: GatedNavItemData[] = [
  {
    icon: "sliders-horizontal",
    label: "Preferences",
    route: "/settings/preferences",
  },
  { icon: "circle-user", label: "Profile", route: "/settings/profile" },
  // Access manages personal API keys — every api-key query/mutation is gated
  // on `workspace.integrations`, so hide the row without that permission.
  {
    icon: "user-lock",
    label: "Access",
    permKey: "workspace.integrations",
    route: "/settings/access",
  },
];

// Each gated item maps to the permission key that controls editing in
// that settings area — if the viewer can't edit the underlying data,
// the row is hidden entirely (consistent with the rest of Permissions
// 2.0 page gating). `discoveryKey` names a discovery flag (see
// useDiscovery) — the row shows a "new feature" dot until the viewer
// opens it, and pressing the row dismisses the flag.
type GatedNavItemData = NavItemData & Omittable<{
  permKey?: string;
  discoveryKey?: string;
}>;

const ISSUE_ITEMS: GatedNavItemData[] = [
  {
    icon: "shapes",
    label: "Types",
    permKey: "issue_types.manage",
    route: "/settings/issue-types",
  },
  {
    icon: "tag",
    label: "Sources",
    permKey: "issue_sources.manage",
    route: "/settings/issue-sources",
  },
];

const FEATURE_ITEMS: GatedNavItemData[] = [
  {
    icon: "refresh-cw",
    label: "Updates",
    permKey: "reasons.manage",
    route: "/settings/updates",
  },
  {
    icon: "trash-2",
    label: "Trash",
    permKey: "trash.manage",
    route: "/settings/trash",
  },
];

const ADMINISTRATION_ITEMS: GatedNavItemData[] = [
  {
    icon: "contact",
    label: "Members",
    permKey: "manage.users",
    route: "/settings/members",
  },
  {
    icon: "users",
    label: "Teams",
    permKey: "manage.teams",
    route: "/settings/teams",
  },
  {
    discoveryKey: "permissions",
    icon: "shield",
    label: "Permissions",
    permKey: "create.roles",
    route: "/settings/permissions",
  },
  {
    exact: true,
    icon: "play-circle",
    label: "Workflows",
    permKey: "manage.edit_workflows",
    route: "/settings/workflows",
  },
  {
    icon: "plug",
    label: "API",
    permKey: "workspace.integrations",
    route: "/settings/api",
  },
];

const CUSTOM_FIELDS_ITEM: GatedNavItemData = {
  icon: "columns-3",
  label: "Custom fields",
  permKey: "custom_fields.manage",
  route: "",
};

// Opens the Integration Hub dialog (no route) — like custom fields.
const INTEGRATIONS_ITEM: GatedNavItemData = {
  icon: "layout-dashboard",
  label: "Integrations",
  permKey: "workspace.integrations",
  route: "",
};

/** One settings destination. `discovery` draws the unseen-feature dot on the
 *  row icon; the row's own handler dismisses it. */
function SettingsNavRow({
  icon,
  label,
  isActive,
  discovery = false,
  onClick,
}: Omittable<{
  icon: IconName;
  label: string;
  isActive: boolean;
  discovery?: boolean;
  onClick: () => void;
}>) {
  // Glyph and label inherit the row's color: SidebarMenuButton keeps an
  // inactive row muted (through hover too) and promotes only the active row,
  // so the selected wash and full-strength text carry the emphasis.
  const iconElement = <Icon name={icon} />;
  return (
    <SidebarMenuItem>
      <SidebarMenuButton isActive={isActive} onClick={onClick}>
        {discovery ? (
          <DiscoveryIndicator>{iconElement}</DiscoveryIndicator>
        ) : (
          iconElement
        )}
        <Text truncate>{label}</Text>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

export function SettingsSidebar() {
  const { replace } = useAppNavigate();
  const { navigate, isActive } = useSidebarNav();
  const { workflows } = useAppData();
  const { openDialog: openCustomFieldsDialog } = useCustomFieldsDialogActions();
  const { openDialog: openIntegrationsDialog } = useIntegrationsDialogActions();
  const closeSidebar = useCloseSidebarIfMobile();
  const sidebarResize = useSidebarResize();

  // Pre-resolve every gated key so we can branch deterministically.
  // Hooks-in-a-loop would be unsafe; explicit lookups are clearer anyway.
  const perms: Record<string, boolean> = {
    "create.roles": usePermission("create.roles"),
    "custom_fields.manage": usePermission("custom_fields.manage"),
    "delete.items": usePermission("delete.items"),
    "issue_sources.manage": usePermission("issue_sources.manage"),
    "issue_types.manage": usePermission("issue_types.manage"),
    "manage.edit_workflows": usePermission("manage.edit_workflows"),
    "manage.teams": usePermission("manage.teams"),
    "manage.users": usePermission("manage.users"),
    "reasons.manage": usePermission("reasons.manage"),
    "trash.manage": usePermission("trash.manage"),
    "workspace.integrations": usePermission("workspace.integrations"),
  };
  const canCreateWorkflows = usePermission("create.workflows");
  const canEditWorkflows = perms["manage.edit_workflows"];

  // Discovery flags, keyed like `perms` above so render loops can branch
  // without hooks-in-a-loop. Add an entry per `discoveryKey` in use.
  const discoveries: Record<string, ReturnType<typeof useDiscovery>> = {
    permissions: useDiscovery("permissions"),
  };
  const itemDiscovery = (item: GatedNavItemData) =>
    item.discoveryKey ? discoveries[item.discoveryKey] : undefined;

  const visibleUserSettings = USER_SETTINGS.filter(
    (item) => !item.permKey || perms[item.permKey],
  );
  const visibleIssueItems = ISSUE_ITEMS.filter(
    (item) => !item.permKey || perms[item.permKey],
  );
  const visibleFeatureItems = FEATURE_ITEMS.filter(
    (item) => !item.permKey || perms[item.permKey],
  );
  const visibleAdminItems = ADMINISTRATION_ITEMS.filter(
    (item) => !item.permKey || perms[item.permKey],
  );
  const showCustomFieldsItem =
    !CUSTOM_FIELDS_ITEM.permKey || perms[CUSTOM_FIELDS_ITEM.permKey];
  const canManageIntegrations =
    !INTEGRATIONS_ITEM.permKey || perms[INTEGRATIONS_ITEM.permKey];
  // Hide the Integrations row when no providers are available (all feature-flag
  // gated off, or the catalog resolves empty). Fetch the catalog only when the
  // user could see the row; the hub dialog shares this query's cache. `providers`
  // is empty during the initial fetch, so the row stays hidden until it resolves
  // (no flash), mirroring the flag-gated UI pattern.
  const { providers: integrationProviders } = useIntegrationCatalog({
    skip: !canManageIntegrations,
  });
  const showIntegrationsItem =
    canManageIntegrations && integrationProviders.length > 0;
  // Per-workflow rows in "Your Workflows" jump straight into that
  // workflow's settings — only useful when the viewer actually has
  // edit-level access (or higher).
  const editableWorkflows = workflows.filter((wf) =>
    canEdit(wf.viewerAccessLevel),
  );

  const navigateBack = () => {
    closeSidebar();
    replace("/");
  };

  const handleOpenCustomFields = () => {
    closeSidebar();
    openCustomFieldsDialog();
  };

  const handleOpenIntegrations = () => {
    closeSidebar();
    openIntegrationsDialog();
  };

  return (
    // The same pane the module menu uses — this replaces it on settings routes,
    // resize seam and all, so the two read as one surface across the swap.
    <SidebarPane {...sidebarResize}>
      {/* An icon-only back button leads, so the title follows it past the
        * reading edge rather than sitting on it. */}
      <SidebarPaneHeader>
        <Button
          aria-label="Back to app"
          onClick={navigateBack}
          size="icon-sm"
          variant="ghost"
        >
          <Icon name="arrow-left" />
        </Button>
        <Text size="lg" truncate weight="semibold">
          Settings
        </Text>
      </SidebarPaneHeader>

      <SidebarPaneBody>
          <SidebarGroup>
            <SidebarMenu>
              {visibleUserSettings.map((item) => (
                <SettingsNavRow
                  icon={item.icon}
                  isActive={isActive(item.route, item.exact)}
                  key={item.route}
                  label={item.label}
                  onClick={() => navigate(item.route)}
                />
              ))}
            </SidebarMenu>
          </SidebarGroup>

          {(visibleIssueItems.length > 0 ||
            visibleFeatureItems.length > 0 ||
            visibleAdminItems.length > 0 ||
            showCustomFieldsItem ||
            showIntegrationsItem ||
            canEditWorkflows) && <SidebarSeparator />}

          {visibleIssueItems.length > 0 && (
            <>
              <SidebarSection title="Projects">
                {visibleIssueItems.map((item) => (
                  <SettingsNavRow
                    icon={item.icon}
                    isActive={isActive(item.route)}
                    key={item.route}
                    label={item.label}
                    onClick={() => navigate(item.route)}
                  />
                ))}
              </SidebarSection>
              <SidebarSeparator />
            </>
          )}

          {visibleFeatureItems.length > 0 && (
            <>
              <SidebarSection title="Features">
                {visibleFeatureItems.map((item) => (
                  <SettingsNavRow
                    icon={item.icon}
                    isActive={isActive(item.route)}
                    key={item.route}
                    label={item.label}
                    onClick={() => navigate(item.route)}
                  />
                ))}
              </SidebarSection>
              <SidebarSeparator />
            </>
          )}

          {(visibleAdminItems.length > 0 ||
            showCustomFieldsItem ||
            showIntegrationsItem) && (
            <SidebarSection title="Administration">
              {visibleAdminItems.map((item) => {
                const discovery = itemDiscovery(item);
                return (
                  <SettingsNavRow
                    discovery={discovery?.visible}
                    icon={item.icon}
                    isActive={isActive(item.route, item.exact)}
                    key={item.route}
                    label={item.label}
                    onClick={() => {
                      discovery?.dismiss();
                      navigate(item.route);
                    }}
                  />
                );
              })}
              {showCustomFieldsItem && (
                <SettingsNavRow
                  icon={CUSTOM_FIELDS_ITEM.icon}
                  isActive={false}
                  label={CUSTOM_FIELDS_ITEM.label}
                  onClick={handleOpenCustomFields}
                />
              )}
              {showIntegrationsItem && (
                <SettingsNavRow
                  icon={INTEGRATIONS_ITEM.icon}
                  isActive={false}
                  label={INTEGRATIONS_ITEM.label}
                  onClick={handleOpenIntegrations}
                />
              )}
            </SidebarSection>
          )}

          {canEditWorkflows && (
            <>
              <SidebarSeparator />
              <SidebarSection title="Your Workflows">
                {editableWorkflows.map((wf) => (
                  <WorkflowItem
                    isActive={isActive(`/settings/workflows/${wf.workflowId}`)}
                    key={wf.workflowId}
                    onClick={() =>
                      navigate(`/settings/workflows/${wf.workflowId}`)
                    }
                    workflow={wf}
                  />
                ))}
                {canCreateWorkflows && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      onClick={() => navigate("/settings/workflows?create=true")}
                    >
                      <Icon color="muted" name="plus" />
                      <Text truncate variant="muted">
                        Create a workflow
                      </Text>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
              </SidebarSection>
            </>
          )}
      </SidebarPaneBody>
    </SidebarPane>
  );
}
